import tw, { styled } from "twin.macro";
import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";

import SubHeader from "app/styles/SubHeader";
import Text from "app/styles/Text";
import {
  BUSHA_RELATED_APP_IDS,
  PAYMENT_LINK,
} from "app/constants/variables";
import { ReactComponent as ArrowIcon } from "app/assets/icons/chevron-right.svg";
import { PaymentMethodType } from "app/providers/types";
import { ReactComponent as BushaLogo } from "app/assets/logo/busha.svg";
import { ReactComponent as WalletIcon } from "app/assets/icons/wallet-outline.svg";
import {
  BUSHA_ROOT,
  TRANSFER_ROOT,
  PAYMENT_PAGE,
  // BUSHA_LOGIN_PAGE,
  // BUSHA_ASSETS_PAGE,
} from "app/constants/routes";
import {
  useAppState,
} from "app/providers/AppStateProvider";
import { getCheckoutDeepLink, IS_MOBILE, openExternalLink } from "app/lib/device";

type PaymentMethodOptionObject = {
  [key in PaymentMethodType]: {
    name: string;
    description: string;
    path: string;
    type: PaymentMethodType;
  };
};

const paymentOptions: PaymentMethodOptionObject = {
  BUSHA: {
    name: "Busha",
    description: "Make payment directly from your busha account",
    path: BUSHA_ROOT,
    type: "BUSHA",
  },
  TRANSFER: {
    name: "Stablecoins",
    description: "Make payment from an external wallet",
    path: `${TRANSFER_ROOT}/${PAYMENT_PAGE}`,
    type: "TRANSFER",
  },
};

export default function PaymentMethods() {
  const { source, sourceId, parentOrigin } = useAppState();

  const [installedBushaAppId, setInstalledBushaAppId] = useState<string>();

  const navigate = useNavigate();

  useEffect(() => {
    const checkInstalledRelatedApps = async () => {
      if (!navigator.getInstalledRelatedApps) return;
      try {
        const relatedApps = await navigator.getInstalledRelatedApps();
        const bushaApp =
          relatedApps?.find((app) =>
            app.id ? BUSHA_RELATED_APP_IDS.find(id => id === app.id) : undefined
          );
        setInstalledBushaAppId(bushaApp?.id);
      } catch {
        setInstalledBushaAppId(undefined);
      }
    };
    checkInstalledRelatedApps();
  }, []);

  const onMethodClicked = (path: string, type: PaymentMethodType) => () => {
    // On mobile, fire the Busha deep link alongside routing to the web login.
    // Fire-and-forget: if the app opens, the user is away and the login page
    // sits behind them; if it doesn't, they're already on the login page with
    // no wait. Desktop has no mobile app to open, so we just route.
    if (type === "BUSHA" && source === PAYMENT_LINK && sourceId && IS_MOBILE) {
      let deepLink: string | undefined;
      if (installedBushaAppId) {
        deepLink = `${installedBushaAppId}://busha.co/checkout?checkoutId=${sourceId}`;
      } else if (!navigator.getInstalledRelatedApps) {
        deepLink = getCheckoutDeepLink(sourceId);
      }
      if (deepLink) openExternalLink(deepLink, { parentOrigin });
    }
    navigate(path);
  };

  const icons = {
    BUSHA: (
      <div className="logo-wrapper">
        <BushaLogo tw="text-text-high" />
      </div>
    ),
    TRANSFER: (
      <div className="logo-wrapper">
        <WalletIcon />
      </div>
    ),
  };

  return (
    <div tw="flex flex-col gap-[12px] pt-[16px]">
      <Text big mediumBold tw="leading-[24px]">Choose a payment method</Text>

      <div tw="flex flex-col gap-[16px]">
        {Object.keys(paymentOptions).map((methodType, index) => {
          const paymentOption = paymentOptions[methodType as PaymentMethodType];

          return (
            <PaymentMethod
              role="button"
              key={methodType}
              onClick={onMethodClicked(
                paymentOption.path,
                paymentOption.type
              )}
            >
              <div className="row-item">
                {icons[paymentOption.type]}

                <div>
                  <div tw="flex items-center">
                    <SubHeader>{paymentOption.name}</SubHeader>
                  </div>

                  <Text tw="mt-[2px]" small subdued>
                    {paymentOption.description}
                  </Text>
                </div>
              </div>

              <ArrowIcon tw="text-text-mid w-[24px] h-[24px]" />
            </PaymentMethod>
          );
        })}
      </div>
    </div>
  );
}


const PaymentMethod = styled.div`
  ${tw`flex justify-between items-center p-[16px] bg-containment-tertiary rounded-[16px]`};

  &.disabled {
    ${tw`cursor-not-allowed pointer-events-none`};
  }

  &.border {
    ${tw`border-b border-containment-secondary`}
  }

  .logo-wrapper {
    ${tw`w-[40px] h-[40px] flex-shrink-0 flex items-center justify-center bg-containment-secondary rounded-full`};
  }

  .logo-wrapper.busha-logo {
    ${tw`bg-primary`};
  }

  .option-desc {
    ${tw`text-icon-textMid`};
  }

  > .row-item {
    ${tw`flex items-center gap-[16px]`};
  }

  > svg {
    ${tw`w-[24px] h-[24px] flex-shrink-0`};
  }

  > div {
    > p {
      ${tw`mt-[4px] text-grey font-medium`};
    }
  }
`;
